import { useState, useEffect } from 'react';
import { useAuth, apiService } from '../AdminContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { 
  Plus, Pencil, Trash2, Save, Search, RefreshCw, MoreVertical,
  Gift, Calendar, Clock
} from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../../components/ui/dropdown-menu';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../../components/ui/alert-dialog';

const defaultOffer = {
  id: '', title: '', short_description: '', full_description: '', image: '',
  start_date: '', end_date: '', is_active: true, show_on_homepage: true, order: 0
};

export const OffersManager = () => {
  const { token } = useAuth();
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [editModal, setEditModal] = useState({ open: false, item: null });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, item: null });

  useEffect(() => { fetchOffers(); }, []);

  const fetchOffers = async () => {
    try {
      const res = await apiService.getOffers(token);
      setOffers(res.data);
    } catch (error) {
      toast.error('Failed to load offers');
    } finally {
      setLoading(false);
    }
  };

  const generateSlug = (name) => name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '').slice(0, 50);

  const handleSave = async () => {
    const { item } = editModal;
    if (!item.id || !item.title) {
      toast.error('Please fill in required fields');
      return;
    }

    try {
      if (item.isNew) {
        await apiService.createOffer(token, item);
        toast.success('Offer created successfully!');
      } else {
        await apiService.updateOffer(token, item.id, item);
        toast.success('Offer updated successfully!');
      }
      setEditModal({ open: false, item: null });
      fetchOffers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error saving offer');
    }
  };

  const handleDelete = async () => {
    try {
      await apiService.deleteOffer(token, deleteDialog.item.id);
      toast.success('Offer deleted');
      setDeleteDialog({ open: false, item: null });
      fetchOffers();
    } catch (error) {
      toast.error('Error deleting offer');
    }
  };

  const openEditModal = (item = null) => {
    setEditModal({
      open: true,
      item: item ? { ...item, isNew: false } : { ...defaultOffer, id: `offer-${Date.now()}`, order: offers.length + 1, isNew: true }
    });
  };

  const getOfferStatus = (offer) => {
    if (!offer.is_active) return { label: 'Inactive', color: 'bg-slate-100 text-slate-700' };
    const now = new Date();
    const start = offer.start_date ? new Date(offer.start_date) : null;
    const end = offer.end_date ? new Date(offer.end_date) : null;
    
    if (start && now < start) return { label: 'Scheduled', color: 'bg-blue-100 text-blue-700' };
    if (end && now > end) return { label: 'Expired', color: 'bg-red-100 text-red-700' };
    return { label: 'Active', color: 'bg-green-100 text-green-700' };
  };

  const filtered = offers.filter(o => 
    o.title.toLowerCase().includes(search.toLowerCase()) || 
    o.short_description?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="space-y-6" data-testid="offers-manager">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="font-['Oswald'] text-2xl font-bold text-slate-900">Offers & Announcements</h1>
          <p className="text-slate-500">Manage promotions, offers, and announcements</p>
        </div>
        <Button onClick={() => openEditModal()} className="bg-orange-500 hover:bg-orange-600">
          <Plus className="w-4 h-4 mr-2" /> Add Offer
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input placeholder="Search offers..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" />
            </div>
            <Button variant="outline" onClick={fetchOffers}><RefreshCw className="w-4 h-4" /></Button>
          </div>
        </CardContent>
      </Card>

      {/* Offers List */}
      {loading ? (
        <div className="flex justify-center py-12"><RefreshCw className="w-8 h-8 animate-spin text-sky-500" /></div>
      ) : filtered.length === 0 ? (
        <Card><CardContent className="py-12 text-center">
          <Gift className="w-12 h-12 text-slate-300 mx-auto mb-4" />
          <p className="text-slate-500">No offers found</p>
          <Button onClick={() => openEditModal()} variant="outline" className="mt-4"><Plus className="w-4 h-4 mr-2" /> Create Offer</Button>
        </CardContent></Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((offer) => {
            const status = getOfferStatus(offer);
            return (
              <Card key={offer.id} className={`overflow-hidden ${!offer.is_active ? 'opacity-60' : ''}`}>
                {offer.image && (
                  <div className="aspect-video relative bg-slate-100">
                    <img src={offer.image} alt={offer.title} className="w-full h-full object-cover" />
                    <div className="absolute top-2 left-2">
                      <span className={`px-2 py-1 text-xs rounded ${status.color}`}>{status.label}</span>
                    </div>
                    <div className="absolute top-2 right-2">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild><Button size="icon" variant="secondary" className="w-8 h-8"><MoreVertical className="w-4 h-4" /></Button></DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => openEditModal(offer)}><Pencil className="w-4 h-4 mr-2" /> Edit</DropdownMenuItem>
                          <DropdownMenuItem onClick={() => setDeleteDialog({ open: true, item: offer })} className="text-red-600"><Trash2 className="w-4 h-4 mr-2" /> Delete</DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>
                )}
                <CardContent className={`p-4 ${!offer.image ? 'pt-4' : ''}`}>
                  {!offer.image && (
                    <div className="flex items-center justify-between mb-2">
                      <span className={`px-2 py-1 text-xs rounded ${status.color}`}>{status.label}</span>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild><Button size="icon" variant="ghost" className="w-8 h-8"><MoreVertical className="w-4 h-4" /></Button></DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => openEditModal(offer)}><Pencil className="w-4 h-4 mr-2" /> Edit</DropdownMenuItem>
                          <DropdownMenuItem onClick={() => setDeleteDialog({ open: true, item: offer })} className="text-red-600"><Trash2 className="w-4 h-4 mr-2" /> Delete</DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  )}
                  <h3 className="font-semibold text-slate-900 line-clamp-1">{offer.title}</h3>
                  <p className="text-sm text-slate-500 line-clamp-2 mt-1">{offer.short_description}</p>
                  {(offer.start_date || offer.end_date) && (
                    <div className="flex items-center gap-2 mt-3 text-xs text-slate-400">
                      <Calendar className="w-3 h-3" />
                      {offer.start_date && <span>From {offer.start_date}</span>}
                      {offer.end_date && <span>to {offer.end_date}</span>}
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Edit Modal */}
      <Dialog open={editModal.open} onOpenChange={(open) => !open && setEditModal({ open: false, item: null })}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editModal.item?.isNew ? 'Create New Offer' : 'Edit Offer'}</DialogTitle>
            <DialogDescription>Fill in the offer details below</DialogDescription>
          </DialogHeader>
          
          {editModal.item && (
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Banner Image (Optional)</Label>
                {editModal.item.image && <img src={editModal.item.image} alt="Preview" className="w-full h-32 object-cover rounded-lg" />}
                <Input value={editModal.item.image || ''} onChange={(e) => setEditModal({ ...editModal, item: { ...editModal.item, image: e.target.value } })} placeholder="Image URL" />
              </div>

              <div className="space-y-2">
                <Label>Offer Title *</Label>
                <Input
                  value={editModal.item.title}
                  onChange={(e) => {
                    const title = e.target.value;
                    const updates = { title };
                    if (editModal.item.isNew) updates.id = generateSlug(title);
                    setEditModal({ ...editModal, item: { ...editModal.item, ...updates } });
                  }}
                  placeholder="e.g., Monsoon Sale - 20% Off"
                />
              </div>

              <div className="space-y-2">
                <Label>Short Description *</Label>
                <Input value={editModal.item.short_description} onChange={(e) => setEditModal({ ...editModal, item: { ...editModal.item, short_description: e.target.value } })} placeholder="Brief description for cards" maxLength={100} />
              </div>

              <div className="space-y-2">
                <Label>Full Description (Optional)</Label>
                <Textarea value={editModal.item.full_description || ''} onChange={(e) => setEditModal({ ...editModal, item: { ...editModal.item, full_description: e.target.value } })} placeholder="Detailed offer information..." rows={3} />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Start Date (Optional)</Label>
                  <Input type="date" value={editModal.item.start_date || ''} onChange={(e) => setEditModal({ ...editModal, item: { ...editModal.item, start_date: e.target.value } })} />
                </div>
                <div className="space-y-2">
                  <Label>End Date (Optional)</Label>
                  <Input type="date" value={editModal.item.end_date || ''} onChange={(e) => setEditModal({ ...editModal, item: { ...editModal.item, end_date: e.target.value } })} />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Display Order</Label>
                <Input type="number" value={editModal.item.order} onChange={(e) => setEditModal({ ...editModal, item: { ...editModal.item, order: parseInt(e.target.value) || 0 } })} />
              </div>

              <div className="flex flex-wrap gap-6 pt-2">
                <div className="flex items-center gap-2">
                  <Switch checked={editModal.item.is_active} onCheckedChange={(checked) => setEditModal({ ...editModal, item: { ...editModal.item, is_active: checked } })} />
                  <Label>Active</Label>
                </div>
                <div className="flex items-center gap-2">
                  <Switch checked={editModal.item.show_on_homepage} onCheckedChange={(checked) => setEditModal({ ...editModal, item: { ...editModal.item, show_on_homepage: checked } })} />
                  <Label>Show on Homepage</Label>
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setEditModal({ open: false, item: null })}>Cancel</Button>
            <Button onClick={handleSave} className="bg-sky-500 hover:bg-sky-600"><Save className="w-4 h-4 mr-2" /> Save Offer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={deleteDialog.open} onOpenChange={(open) => !open && setDeleteDialog({ open: false, item: null })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Offer?</AlertDialogTitle>
            <AlertDialogDescription>Are you sure you want to delete "{deleteDialog.item?.title}"? This action cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-500 hover:bg-red-600">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default OffersManager;
